
import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { SmsCustomer } from './types';
import { toast } from '@/components/ui/use-toast';

interface CacheEntry {
  data: SmsCustomer[];
  timestamp: number;
  fetchParams: string;
}

const globalCache = new Map<string, CacheEntry>();
const CACHE_TIME = 300000;
let currentFetchPromise: Promise<any> | null = null;
let lastFetchTimestamp = 0;
const FETCH_DEBOUNCE = 500;

export const useSmsSubscribers = (user?: any, selectedRestaurantIds?: string[]) => {
  const [subscribers, setSubscribers] = useState<SmsCustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const selectedRestaurantIdsRef = useRef<string[] | undefined>(selectedRestaurantIds);
  const userIdRef = useRef<string | undefined>(user?.id);

  useEffect(() => {
    const currentIds = selectedRestaurantIdsRef.current || [];
    const newIds = selectedRestaurantIds || [];
    
    if (user?.id !== userIdRef.current || 
        JSON.stringify([...currentIds].sort()) !== JSON.stringify([...newIds].sort())) {
      selectedRestaurantIdsRef.current = selectedRestaurantIds;
      userIdRef.current = user?.id;
    }
  }, [user?.id, selectedRestaurantIds]);

  const fetchSubscribers = useCallback(async (forceRefresh = false, targetSubscriberId?: string) => {
    if (!user) {
      setSubscribers([]);
      setLoading(false);
      return;
    }

    const organisationId = user.organisationId || null;
    const restaurantIds = selectedRestaurantIdsRef.current || 
      (user.restaurants?.map((r: any) => r.id) || []);
    
    const fetchParams = JSON.stringify({
      organisationId,
      restaurantIds: restaurantIds.length > 0 ? [...restaurantIds].sort() : 'all'
    });
    
    const cacheKey = `subscribers-${user.id}-${fetchParams}`;
    
    // Validate targetSubscriberId is a string if provided
    const validTargetId = targetSubscriberId && typeof targetSubscriberId === 'string' ? targetSubscriberId : null;
    
    // Enhanced logging for targeted subscriber refresh
    if (validTargetId) {
      console.log(`[SMS-SUBSCRIBERS] Targeted refresh requested for subscriber: ${validTargetId}`);
    }
    
    // Log cache invalidation attempts for debugging
    if (forceRefresh) {
      console.log(`[SMS-SUBSCRIBERS] Force refreshing data, invalidating cache for key: ${cacheKey}`);
      
      // Remove this specific cache entry when forceRefresh is true
      if (globalCache.has(cacheKey)) {
        globalCache.delete(cacheKey);
        console.log(`[SMS-SUBSCRIBERS] Cache invalidated successfully for key: ${cacheKey}`);
      }
    } else if (globalCache.has(cacheKey)) {
      const cachedData = globalCache.get(cacheKey)!;
      const now = Date.now();
      
      // Check if we're targeting a specific subscriber for refresh but still using cache
      if (!forceRefresh && validTargetId) {
        console.log(`[SMS-SUBSCRIBERS] Checking cache for targeted subscriber: ${validTargetId}`);
        
        // Use cached data but potentially update targeted subscriber
        setSubscribers(cachedData.data);
        setLoading(false);
        
        // If we have a specific subscriber to check and caching time is still valid,
        // don't block the UI but continue fetching in background to update just that entry
        if (now - cachedData.timestamp < CACHE_TIME) {
          console.log(`[SMS-SUBSCRIBERS] Using cached data but will refresh targeted subscriber in background`);
          // Continue with fetch but don't show loading indicator
        } else {
          return; // Use cache and stop here - the force refresh will do a full update
        }
      } else if (now - cachedData.timestamp < CACHE_TIME) {
        console.log('Using cached subscribers data, age:', 
          Math.round((now - cachedData.timestamp) / 1000), 's');
        setSubscribers(cachedData.data);
        setLoading(false);
        return;
      } else {
        console.log(`[SMS-SUBSCRIBERS] Cache expired for key: ${cacheKey}, fetching fresh data`);
      }
    }
    
    // When a force refresh is requested, we need to cancel any in-progress fetch
    // to ensure we get the latest data without race conditions
    if (forceRefresh && currentFetchPromise) {
      console.log('[SMS-SUBSCRIBERS] Force refresh requested while fetch in progress, cancelling previous fetch');
      currentFetchPromise = null;
    } else if (currentFetchPromise) {
      console.log('[SMS-SUBSCRIBERS] Subscriber fetch already in progress, waiting for result');
      try {
        await currentFetchPromise;
        // After waiting for the current fetch to complete, we need to check again if forceRefresh was requested
        if (forceRefresh) {
          console.log('[SMS-SUBSCRIBERS] Force refresh still needed after waiting for in-progress fetch');
          // Continue to new fetch below
        } else {
          return; // Use the recently fetched data
        }
      } catch (err) {
        console.log('[SMS-SUBSCRIBERS] Previous fetch failed, retrying');
      }
    }
    
    const now = Date.now();
    // Skip debounce for force refresh to ensure immediate updates
    if (!forceRefresh && now - lastFetchTimestamp < FETCH_DEBOUNCE) {
      console.log('[SMS-SUBSCRIBERS] Debouncing fetch request - too soon after previous request');
      return;
    }
    lastFetchTimestamp = now;
    
    setLoading(true);
    setError(null);

    try {
      console.log('Fetching SMS subscribers for:', {
        userId: user.id,
        organisationId,
        restaurantIds: restaurantIds.length > 0 ? restaurantIds : 'all'
      });
      
      if (!organisationId && restaurantIds.length === 0) {
        console.warn('No organization or restaurant access available');
        setSubscribers([]);
        setLoading(false);
        return;
      }

      // Use the optimized function for 24x performance improvement
      const fetchPromise = Promise.resolve(supabase.rpc('get_sms_subscribers_fast', {
        p_organisation_id: organisationId,
        p_restaurant_ids: restaurantIds.length > 0 ? restaurantIds : null
      }));
      
      currentFetchPromise = fetchPromise;
      
      const { data, error: queryError } = await fetchPromise;
      
      currentFetchPromise = null;

      if (queryError) {
        console.error('Error fetching SMS subscribers:', queryError);
        throw new Error(queryError.message || 'Failed to fetch subscribers');
      }

      // Customer creation dates are already included in the materialized view data
      console.log('Using customer creation dates from materialized view for optimal performance');

      // Group subscribers by ID
      const subscribersMap = new Map();
      
      data.forEach((item: any) => {
        const subscriberId = item.id;
        
        if (!subscribersMap.has(subscriberId)) {
          subscribersMap.set(subscriberId, {
            id: item.id,
            name: item.name || 'Unknown',
            phone: item.phone || 'No phone',
            status: mapSubscriptionStatus(item.subscription_status),
            lastOrder: item.latest_order_date ? item.latest_order_date : 'Never',
            lastOrderFormatted: item.latest_order_date ? new Date(item.latest_order_date).toLocaleDateString() : 'Never',
            messageCount: item.total_messages || 0,
            totalOrders: item.total_orders || 0,
            totalSpend: item.total_spend ? Number(item.total_spend).toFixed(2) : '0.00',
            restaurant_ids: [item.restaurant_id],
            email: item.email,
            opt_in_date: item.marketing_opt_in_date || undefined,
            opt_in_date_formatted: item.marketing_opt_in_date ? new Date(item.marketing_opt_in_date).toLocaleDateString() : undefined,
            // PHASE 4: Use real opt-out data instead of approximations
            opt_out_date: item.opt_out_timestamp || undefined,
            opt_out_date_formatted: item.opt_out_timestamp ? new Date(item.opt_out_timestamp).toLocaleDateString() : undefined,
            opt_out_source: item.opt_out_source || undefined,
            last_message_sent: item.last_message_date || undefined,
            last_message_sent_formatted: item.last_message_date ? new Date(item.last_message_date).toLocaleDateString() : undefined,
            customer_created_at: item.customer_created_at || undefined, // Use data from materialized view
            loyaltyTier: determineLoyaltyTier(item.total_orders, item.total_spend),
            opt_in_source: formatOptInSource(item.opt_in_source),
            opt_out_source_formatted: formatOptOutSource(item.opt_out_source)
          });
        } else {
          // If we've seen this subscriber before, add this restaurant ID to their array
          const existingSubscriber = subscribersMap.get(subscriberId);
          
          // Update with most recent data if available
          if (item.latest_order_date && (!existingSubscriber.lastOrder || existingSubscriber.lastOrder === 'Never' ||
              new Date(item.latest_order_date) > new Date(existingSubscriber.lastOrder))) {
            existingSubscriber.lastOrder = item.latest_order_date;
            existingSubscriber.lastOrderFormatted = new Date(item.latest_order_date).toLocaleDateString();
          }
          
          // Add to total orders and spend
          existingSubscriber.totalOrders += item.total_orders || 0;
          
          // Update total spend
          const currentSpend = parseFloat(existingSubscriber.totalSpend) || 0;
          const additionalSpend = item.total_spend ? parseFloat(item.total_spend) : 0;
          existingSubscriber.totalSpend = (currentSpend + additionalSpend).toFixed(2);
          
          // Add restaurant ID if it's not already in the array
          if (!existingSubscriber.restaurant_ids.includes(item.restaurant_id)) {
            existingSubscriber.restaurant_ids.push(item.restaurant_id);
          }
        }
      });
      
      const transformedData = Array.from(subscribersMap.values());
      
      // If we have a targeted subscriber ID, log if we found it in the data
      if (targetSubscriberId && typeof targetSubscriberId === 'string') {
        const targetedSubscriber = transformedData.find(sub => sub.id === targetSubscriberId);
        console.log(`[SMS-SUBSCRIBERS] Targeted refresh for ${targetSubscriberId}: ${targetedSubscriber ? 'Found in data' : 'Not found in data'}`);
        if (targetedSubscriber) {
          console.log(`[SMS-SUBSCRIBERS] Current status for ${targetSubscriberId}: ${targetedSubscriber.status}`);
        }
      }
      
      console.log(`Fetched ${data?.length || 0} SMS subscribers, consolidated to ${transformedData.length} unique customers`);
      
      globalCache.set(cacheKey, {
        data: transformedData,
        timestamp: Date.now(),
        fetchParams
      });
      
      setSubscribers(transformedData);
    } catch (err) {
      console.error('Error in useSmsSubscribers:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      toast({
        title: "Failed to load SMS subscribers",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive"
      });
      
      setSubscribers([]);
      
      currentFetchPromise = null;
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Helper function to map subscription status from database to frontend format
  // Uses the computed subscription_status column from mv_sms_subscribers materialized view
  // This ensures consistent status calculation between database and frontend
  const mapSubscriptionStatus = (subscriptionStatus: string): string => {
    switch (subscriptionStatus) {
      case 'active': return 'subscribed';
      case 'opted_out': return 'unsubscribed';
      case 'failed': return 'invalid';
      case 'not_synced': return 'invalid';
      default: 
        console.warn('Unknown subscription status:', subscriptionStatus);
        return 'invalid';
    }
  };

  // Helper function to determine loyalty tier based on order history
  const determineLoyaltyTier = (totalOrders?: number, totalSpend?: number | string): string => {
    const orders = totalOrders || 0;
    const spend = typeof totalSpend === 'string' ? parseFloat(totalSpend || '0') : (totalSpend || 0);

    if (orders >= 20 || spend >= 1000) return 'VIP';
    if (orders >= 10 || spend >= 500) return 'Gold';
    if (orders >= 5 || spend >= 250) return 'Silver';
    return 'Standard';
  };
  
  // Helper function to format opt-in source for display
  const formatOptInSource = (source?: string | null): string => {
    switch (source) {
      case 'order_checkout': return 'Subscribed at Checkout';
      case 'admin_action': return 'Toggled by Admin';
      case 'sms_stop': return 'Replied STOP to SMS';
      case 'migration': return 'Migrated Subscriber';
      case null: 
      case undefined: 
      case '': return 'Not subscribed';
      default: return source;
    }
  };

  // PHASE 4: Helper function to format opt-out source for display
  const formatOptOutSource = (source?: string | null): string => {
    switch (source) {
      case 'sms_stop': return 'Replied STOP to SMS';
      case 'admin_action': return 'Unsubscribed by Admin';
      case null:
      case undefined:
      case '': return 'Unknown';
      default: return source;
    }
  };

  useEffect(() => {
    if (user) {
      fetchSubscribers();
    }
  }, [user, fetchSubscribers]);


  return { 
    subscribers, 
    loading, 
    error, 
    // Standard force refresh - refreshes all subscribers
    refreshSubscribers: (force = true) => fetchSubscribers(force),
    // Targeted refresh with proper type checking
    refreshTargetedSubscriber: (subscriberId: string) => {
      // Validate subscriberId is a proper string
      if (!subscriberId || typeof subscriberId !== 'string') {
        console.warn(`[SMS-SUBSCRIBERS] Invalid subscriber ID format, expected string but got: ${typeof subscriberId}`);
        return; // Don't refresh if ID format is wrong
      }
      
      // Force a refresh of data but focus on this specific subscriber
      fetchSubscribers(true, subscriberId);
    }
  };
};

export default useSmsSubscribers;
